import Marquee from "@/components/ui/marquee";
import useMusicManager from "@/hooks/useMusicManager";
import deepFocusStore from "@/stores/deepFocusStore";
import { useSelector } from "@tanstack/react-store";
import {
    ChevronLeft,
    ChevronRight,
    Headphones,
    PauseIcon,
    PlayIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import DeepFocusTooltip from "./DeepFocusTooltip";

interface TitleDisplayProps {
    className?: string;
    play: boolean;
    title: string;
    shouldMarquee: boolean;
}

const TitleDisplay = ({
    className = "",
    title,
    shouldMarquee,
    play,
}: TitleDisplayProps) => (
    <div className={`w-full overflow-hidden ${className}`}>
        {shouldMarquee ? (
            <Marquee play={play} speed={15}>
                <p className="whitespace-nowrap px-2 text-xs">{title}</p>
            </Marquee>
        ) : (
            <p className="whitespace-nowrap px-2 text-xs">{title}</p>
        )}
    </div>
);

const DiskJockey = () => {
    const { t } = useTranslation();
    const { currentTrack, isPlaying, play, pause, next, previous } =
        useMusicManager();
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    const isDeepFocusActive = useSelector(
        deepFocusStore,
        (state) => state.isActive,
    );

    const trackTitle = currentTrack?.title || t("deepFocus.diskJockey.noTrack");
    const shouldMarquee = trackTitle.length >= 11;

    useEffect(() => {
        if (!isDeepFocusActive && isPlaying) {
            pause();
        }
    }, [isDeepFocusActive]);

    return (
        <DeepFocusTooltip
            action={
                <div className="flex items-center gap-2">
                    {isPlaying ? (
                        <PauseIcon className="size-4" />
                    ) : (
                        <PlayIcon className="size-4" />
                    )}
                    <p>
                        {isPlaying
                            ? t("deepFocus.diskJockey.pause")
                            : t("deepFocus.diskJockey.play")}
                    </p>
                </div>
            }
            content={
                <div className="group flex w-full flex-row items-center justify-between px-2">
                    <Button
                        onClick={previous}
                        size="tinyCircle"
                        variant="invisible"
                    >
                        <ChevronLeft className="size-4" />
                    </Button>
                    <TitleDisplay
                        play={isTooltipOpen}
                        shouldMarquee={shouldMarquee}
                        title={trackTitle}
                    />
                    <Button
                        onClick={next}
                        size="tinyCircle"
                        variant="invisible"
                    >
                        <ChevronRight className="size-4" />
                    </Button>
                </div>
            }
            handleAction={isPlaying ? pause : play}
            onOpenChange={setIsTooltipOpen}
        >
            <Headphones className="size-4 w-4" />
            <TitleDisplay
                className="flex items-center justify-center max-w-15"
                play={isTooltipOpen}
                shouldMarquee={shouldMarquee}
                title={trackTitle}
            />
        </DeepFocusTooltip>
    );
};

export default DiskJockey;
